import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    Output
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { I18NextPipe } from '@kokos-i18next';
import i18next from 'i18next';

import { vNiveauEnum } from '../../../core/model/nachricht/vniveau.enum';
import { OrdnerEnum } from '../../../core/model/ordner.enum';
import { NachrichtenTypeEnum } from '../../../core/model/nachricht/nachrichten-type.enum';
import { NachrichtActionButtonsComponent } from '../nachricht-action-buttons/nachricht-action-buttons.component';
import { NachrichtAction } from '../nachricht-action-buttons/nachricht-action.enum';
import {I18nRerenderOnLanguageChangeDirective} from "../../directives/i18n-rerender-on-language-change.directive";

@Component({
    selector: 'pf-nachricht-list-item',
    imports: [RouterLink, NachrichtActionButtonsComponent, I18NextPipe, I18nRerenderOnLanguageChangeDirective],
    templateUrl: './nachricht-list-item.component.html',
    styleUrls: ['./nachricht-list-item.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NachrichtListItemComponent {
    @Input({ required: true }) nachrichtId!: string;
    @Input() gelesen = false;
    @Input() mitAnhang = false;
    @Input({ required: true }) dateTime!: string;
    @Input({ required: true }) betreff!: string;
    @Input() bezug = '';
    @Input() showBezug = true;
    @Input() vNiveauAusreichend = false;
    @Input({ required: true }) trustLevel!: vNiveauEnum;
    @Input() nachrichtActions: NachrichtAction[] = [];
    @Input({ required: true }) nachrichtFolder!: OrdnerEnum;
    @Input({ required: true }) nachrichtReadLink!: string;
    @Input({ required: true }) nachrichtType!: NachrichtenTypeEnum;
    @Input() erstleser: string = null;
    @Input() vertreter: string = null;

    @Output() buttonClicked = new EventEmitter<NachrichtAction>();
    @Output() stepUpClicked = new EventEmitter<boolean>();

    protected readonly vNiveauEnum = vNiveauEnum;
    protected readonly OrdnerEnum = OrdnerEnum;
    protected readonly NachrichtenTypeEnum = NachrichtenTypeEnum;
    protected readonly NachrichtAction = NachrichtAction;

    stepUp (event: MouseEvent): void {
        event.preventDefault();
        this.stepUpClicked.emit()
    }

    getTrustLevelIcon(): string {
        return this.trustLevel === vNiveauEnum.HOCH
            ? 'ba-icon-trust-high'
            : 'ba-icon-trust-medium';
    }

    get title(): string | null {
        const parts: string[] = [];

        if (this.vertreter) {
            parts.push(this.vertreterLabel);
        }

        if (!this.vNiveauAusreichend) {
            parts.push(this.trustLevelLabel);
        }

        return parts.length ? parts.join(', ') : null;
    }

    get ariaLabel(): string {
        const parts: string[] = [
            this.betreff,
            this.dateTime,
            this.bezug,
            this.getAttachmentLabel()
        ];

        const readStatus = this.getReadStatusLabel();
        if (readStatus) {
            parts.push(readStatus);
        }
        if (this.erstleser) {
            parts.push(this.erstLeserDisplay);
        }

        if (this.vertreter) {
            parts.push(this.vertreterDisplay);
        }

        if (!this.vNiveauAusreichend) {
            parts.push(this.trustLevelLabel);
        }

        return parts.join(', ');
    }

    get vertreterLabel():string{
        return i18next.t('mailbox.acting-for');
    }

    get vertreterDisplay(): string {
        return `${i18next.t('mailbox.last-edited-by')} ${this.vertreter}`;
    }

    get erstLeserDisplay():string{
        return `${i18next.t('mailbox.first-read-by')} ${this.erstleser}`;
    }

    get trustLevelLabel(): string {
        const required = `${i18next.t('mailbox.trustlevel.required')} `;

        switch (this.trustLevel) {
            case vNiveauEnum.SUBSTANZIELL:
                return required + i18next.t('mailbox.trustlevel.substantial');
            case vNiveauEnum.HOCH:
                return required + i18next.t('mailbox.trustlevel.high');
            default:
                return required + i18next.t('mailbox.trustlevel.normal');
        }
    }

    private getAttachmentLabel(): string {
        return this.mitAnhang
            ? i18next.t('mailbox.with-attachment')
            : i18next.t('mailbox.without-attachment');
    }

    private getReadStatusLabel(): string | null {
        if (this.nachrichtFolder !== OrdnerEnum.ERHALTEN) {
            return null;
        }

        return this.gelesen
            ? i18next.t('mailbox.read')
            : i18next.t('mailbox.unread');
    }
}